// Features: M004-F0713, M004-F0714, M004-F0721. Tests: T-M004-0205, T-M004-0206, T-M004-0208, T-M004-0227.
//
// The ONE place the transaction-history wire lives (design M004-F0712-F0716 section 6), like ledgerQueuesAdapter.js.
// Shapes: Spec core-ledger-account-gateway 1.5.0 (digest 3014cc73...), operations
//   getBillingTransactionHistory  GET  /v1/orgs/{orgId}/billing/transactions/{key}            billing.view + membership (ACCT-TX-006)
//   getLedgerTransactionHistory   GET  /v1/orgs/{orgId}/ledger/users/{userId}/transactions/{handOffId}/history   (SAL 3, ledger_manage)
//   raiseLedgerCreditNote         POST /v1/orgs/{orgId}/ledger/users/{userId}/transactions/{handOffId}/credit-notes (ACCT-TX-021)
// Core's sentences (headline, from, summary, next_action, conditions[], can.*.reason) are carried UNCHANGED: this module
// never composes, trims or re-cases one (ACCT-TX-011). The delivered timeline order is never re-sorted (ACCT-TX-006).
// Every request built here is validated by Spec's generated client in the tests (tests/ledger-account-gateway/conformance.mjs).
import { displayTxId } from '../utils/transactionId';
import { isTxCreditNoteBackendAvailable } from '../utils/txFeatureFlags';

const FULL_HAND_OFF_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const orgBase = (orgId) => `/v1/orgs/${encodeURIComponent(orgId)}`;
const ledgerBase = (orgId, userId) => `${orgBase(orgId)}/ledger/users/${encodeURIComponent(userId)}`;

export function buildTxHistoryApi(http) {
  return {
    // `key` = `TX-xxxxxxxx` or the full id, never both (design 2.2). No caching: the drawer re-reads on every open.
    // `userId` (1.5.0, ACCT-TX-022) is the customer: Core uses it ONLY on the first read of a Ledger-minted TX it holds no row for
    // (it addresses the per-customer Ledger route to adopt it, ACCT-TX-015). Optional, ignored otherwise, never widens access.
    // Sent only with a full hand_off_id (adoption is by the full id, never by a short TX key).
    getHistory: (orgId, key, userId) => http.get(`${orgBase(orgId)}/billing/transactions/${encodeURIComponent(key)}${
      userId != null && userId !== '' && FULL_HAND_OFF_ID.test(String(key)) ? `?user_id=${encodeURIComponent(userId)}` : ''}`),
    getLedgerHistory: (orgId, userId, handOffId) => http.get(`${ledgerBase(orgId, userId)}/transactions/${encodeURIComponent(handOffId)}/history`),
    // SAL 4 + Idempotency-Key (one key per intent, reused on retry and on the step-up replay).
    raiseCreditNote: (orgId, userId, handOffId, { amountMinor, reason }, idempotencyKey) => http.post(
      `${ledgerBase(orgId, userId)}/transactions/${encodeURIComponent(handOffId)}/credit-notes`,
      { amount_minor: amountMinor, reason },
      { headers: { 'Idempotency-Key': idempotencyKey } },
    ),
  };
}

export const unwrap = (response) => {
  const body = response?.data;
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) return body.data;
  return body ?? null;
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const list = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (typeof value === 'string' ? value : '');

// Documents (`children[]`) from the Core body AND the Ledger half, merged by `document_ref`, never twice (design 2.5).
// A child with no document_ref is a separate TX-valued child and is kept as given (deduped by hand_off_id).
export function mergeChildren(coreChildren, ledgerChildren) {
  const seen = new Set();
  const out = [];
  [...list(coreChildren), ...list(ledgerChildren)].forEach((child) => {
    if (!isObject(child)) return;
    const key = child.document_ref ? `doc:${child.kind}:${child.document_ref}` : `tx:${child.hand_off_id || child.tx_id || ''}:${child.kind}:${child.at || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(child);
  });
  return out;
}

// getBillingTransactionHistory `data` -> the view model the drawer and TxTimeline render. Nothing is derived: members are
// passed through (arrays defaulted so a half that did not load never reads as "empty").
export function normaliseTxHistory(data) {
  if (!isObject(data) || (typeof data.hand_off_id !== 'string' && typeof data.tx_id !== 'string')) return null;
  const ledger = isObject(data.ledger) ? data.ledger : { available: false, reason: 'unknown', message: null };
  const ledgerState = ledger.available === true && isObject(ledger.state) ? ledger.state : null;
  const can = isObject(data.can) ? data.can : null;
  return {
    ledgerOnly: false,
    txId: displayTxId({ txId: data.tx_id, handOffId: data.hand_off_id }),
    handOffId: text(data.hand_off_id),
    userId: Number.isFinite(Number(data.user_id)) && data.user_id != null ? Number(data.user_id) : null,
    parentHandOffId: text(data.parent_hand_off_id),
    kind: text(data.kind),
    title: text(data.title),
    window: isObject(data.window) ? data.window : null,
    room: isObject(data.room) ? data.room : null,
    stage: text(data.stage),
    coreStage: text(data.core_stage),
    ledgerStage: text(data.ledger_stage),
    stageDetail: text(data.stage_detail),
    headline: text(data.headline),
    from: text(data.from),
    nextAction: text(data.next_action),
    conditions: list(data.conditions),
    consistency: isObject(data.consistency) ? data.consistency : { ok: true, issue: null },
    origin: isObject(data.origin) ? data.origin : null,
    pricing: isObject(data.pricing) ? data.pricing : null,
    can,
    canViewAmounts: can ? can.view_amounts?.allowed === true : false,
    timeline: list(data.timeline),
    journalPartial: data.journal_partial === true,
    journalTruncated: ledgerState?.journal_truncated === true,
    ledger: { available: ledger.available === true, reason: text(ledger.reason), message: typeof ledger.message === 'string' ? ledger.message : '', state: ledgerState },
    children: mergeChildren(data.children, ledgerState?.children),
    invoice: ledgerState?.invoice || null,
    ledgerEntryId: ledgerState?.ledger_entry_id || null,
    payments: list(ledgerState?.payments),
    timezone: text(data.window?.timezone) || text(data.origin?.timezone),
  };
}

// getLedgerTransactionHistory `data` -> the same model, Ledger half only (the Ledger-only fallback, design 2.7).
// The timeline is the Ledger's own journal, in the order the Ledger delivered it.
export function normaliseLedgerOnlyHistory(data) {
  if (!isObject(data) || !isObject(data.state)) return null;
  const state = data.state;
  return {
    ledgerOnly: true,
    txId: displayTxId({ txId: data.tx_id, handOffId: data.hand_off_id }),
    handOffId: text(data.hand_off_id),
    userId: null,
    parentHandOffId: '',
    kind: '', title: '', window: null, room: null,
    stage: text(state.stage), coreStage: '', ledgerStage: text(state.stage), stageDetail: text(state.stage_detail),
    headline: '', from: '', nextAction: '', conditions: [],
    consistency: { ok: true, issue: null },
    origin: null, pricing: null, can: null, canViewAmounts: true,
    timeline: list(state.journal),
    journalPartial: false,
    journalTruncated: state.journal_truncated === true,
    ledger: { available: true, reason: '', message: '', state },
    children: mergeChildren([], state.children),
    invoice: state.invoice || null,
    ledgerEntryId: state.ledger_entry_id || null,
    payments: list(state.payments),
    timezone: '',
  };
}

// Accept either wire data or an already-normalised model (TxTimeline is mounted by several hosts).
export function asTxModel(input) {
  if (!input || typeof input !== 'object') return null;
  if ('ledgerOnly' in input && 'timeline' in input && 'ledger' in input) return input;
  if (isObject(input.state) && !('timeline' in input)) return normaliseLedgerOnlyHistory(input);
  return normaliseTxHistory(input);
}

// ---- Errors of the Core history read (design 2.7) ----
export const errorStatus = (error) => Number(error?.response?.status) || 0;
export const errorBody = (error) => (isObject(error?.response?.data) ? error.response.data : {});
export const errorCode = (error) => {
  const body = errorBody(error);
  if (typeof body.error === 'string') return body.error;
  return body.error?.code || body.code || '';
};

// -> { kind: 'ambiguous'|'not_found'|'forbidden'|'bad_key'|'step_up'|'failed', candidates? }
export function classifyHistoryError(error) {
  const status = errorStatus(error);
  const code = errorCode(error);
  const body = errorBody(error);
  if (status === 409 && (code === 'TX_ID_AMBIGUOUS' || Array.isArray(body.candidates) || Array.isArray(body.details?.candidates))) {
    return { kind: 'ambiguous', candidates: list(body.candidates || body.details?.candidates) };
  }
  if (status === 404) return { kind: 'not_found' };
  if (status === 403) return { kind: 'forbidden' };
  if (status === 400) return { kind: 'bad_key' };
  if (status === 401 && /STEP_UP/i.test(code)) return { kind: 'step_up' };
  return { kind: 'failed' };
}

// Stages at which the Ledger has WRITTEN the entry: the only ones a credit note applies to (design 4.7b).
export const WRITTEN_STAGES = Object.freeze(['posted', 'paid', 'credited']);

// "Raise credit note" is offered from the LEDGER half only: the Ledger state is present (it is shown only to a ledger_manage viewer,
// the history read answers `not_permitted` otherwise) and says written and not paid. NEVER from Core's can.edit_amount.
export const CREDITABLE_LEDGER_STAGES = Object.freeze(['posted', 'credited']);
export function ledgerWritten(model) {
  const state = model?.ledger?.available === true ? model.ledger.state : null;
  if (!state) return false;
  return CREDITABLE_LEDGER_STAGES.includes(text(state.stage) || model.ledgerStage);
}
// Release gate (design 4.7b): the credit-note action additionally needs the build flag, off until Core #356 serves the route.
// "Bill more under this TX" is a separate action and keeps using ledgerWritten.
export const creditNoteOffered = (model) => isTxCreditNoteBackendAvailable() && ledgerWritten(model);

// What is left to credit, computed ONLY from amounts the drawer already shows: the TX amount less its credit-note documents.
// null when the amount is unknown (no pricing, or amounts hidden): the Ledger then decides.
export function remainingToCreditMinor(model) {
  const total = model?.pricing?.amount_minor;
  if (!model?.canViewAmounts || !Number.isFinite(Number(total)) || total == null) return null;
  const credited = list(model.children).filter((c) => c?.kind === 'credit_note').reduce((sum, c) => sum + (Number(c.amount_minor) || 0), 0);
  return Math.max(0, Number(total) - credited);
}

// A 200 from raiseLedgerCreditNote carries the Ledger's fresh `state`: apply it (confirmed UI), keeping Core's half as it was.
export function applyLedgerState(model, state) {
  if (!model || !isObject(state)) return model;
  const children = mergeChildren(model.children, state.children);
  return {
    ...model,
    stage: text(state.stage) || model.stage,
    ledgerStage: text(state.stage) || model.ledgerStage,
    stageDetail: text(state.stage_detail),
    ledger: { available: true, reason: '', message: '', state },
    children,
    invoice: state.invoice || model.invoice,
    ledgerEntryId: state.ledger_entry_id || model.ledgerEntryId,
    payments: list(state.payments),
    journalTruncated: state.journal_truncated === true,
  };
}
